/**
 * The drawer, as the till holds it.
 *
 * Specification: ../test/drawer.spec.ts. Master plan §4 M8, §3 K10.
 *
 * `@gf/module-cash` has had the whole model since it was written — folds, blind
 * counts, variance, fraud statistics — and no screen. This is the piece
 * between: it turns a tap into a shift event, keeps the fold current, and
 * decides what the person holding the handheld is allowed to see.
 *
 * IT SITS IN THE EMIT PATH. Every event the till produces passes through
 * `observe` on its way to the outbox, which is how cash sales are DERIVED from
 * the same `tender.recorded` events the server will fold rather than counted
 * alongside them. A parallel accumulator agrees on every ordinary evening and
 * disagrees on exactly the one somebody asks about — the mistake archive/v0
 * made, and the reason the module refuses to accept totals in a close payload.
 *
 * BLIND MEANS BLIND ON THIS SIDE TOO. The module makes `CountSheet`
 * structurally incapable of carrying an expectation, and that is not enough
 * here, because the till holds the events and could compute one at any moment.
 * So `report()` returns null until a closing count exists: a screen cannot
 * render a number the session will not give it.
 *
 * WHAT IT REFUSES TO EMIT, AND WHY THAT IS NOT DUPLICATION. `foldShift` records
 * ALREADY_OPEN, MOVEMENT_WITHOUT_REASON, SAFE_DROP_UNWITNESSED and
 * NEGATIVE_AMOUNT as anomalies, which is the right answer for an event that
 * REACHED the server — the fact happened and hiding it helps nobody. It is the
 * wrong answer for a double tap on the open button. These guards stop the event
 * being created; the fold stays the authority on everything that was.
 */

import {
  CZK_DENOMINATIONS,
  classifyVariance,
  countSheetFor,
  countTotal,
  expectedDrawer,
  foldShift,
  type CashMovementKind,
  type CountKind,
  type CountSheet,
  type DenominationCount,
  type DrawerBreakdown,
  type DrawerModel,
  type CashSalesFacts,
  type ShiftEvent,
  type ShiftState,
  type VariancePolicy,
  type VarianceAssessment,
} from "@gf/module-cash/shift";
// The SHIFT subpath, not the barrel. The barrel also re-exports ./detection —
// median, MAD, modified z-scores, cohort assessment — which is back-office
// fraud analytics a till never runs and would carry in its startup bundle.
import type { OutboundEvent } from "@gf/sync-client";

/**
 * Where the drawer keeps the events it folds, so a reload does not lose them.
 *
 * SEPARATE FROM THE OUTBOX, and the distinction is the whole reason this
 * exists. The outbox is a TRANSMISSION queue: an event leaves it the moment the
 * server acknowledges. On a till that has been draining happily all evening it
 * holds nothing, so a drawer rebuilt from it would report a shift that took no
 * money.
 *
 * This is a local read model, not a second source of truth. The server still
 * derives its own expectation from the event log, and the two still have to
 * agree — which is what the e2e checks.
 */
export interface DrawerJournal {
  append(event: OutboundEvent): Promise<void>;
  load(): Promise<OutboundEvent[]>;
  reset(): Promise<void>;
}

export interface DrawerDeps {
  /** The real outbox, or anything that accepts an event. */
  readonly outbox: { enqueue(event: OutboundEvent): Promise<void> };
  /**
   * Optional. Absent when OPFS is unavailable — a till that refused to open a
   * drawer because it could not persist one would be a venue that cannot trade,
   * so it degrades to an in-memory shift rather than to no shift.
   */
  readonly journal?: DrawerJournal;
  readonly now: () => Date;
  readonly newId: () => string;
  readonly nextHlc: () => string;
  /**
   * Derived on the device from the venue clock in the signed config bundle,
   * using the same `businessDateOf` the server uses — so the two agree by
   * construction rather than by luck.
   */
  readonly businessDate: () => string;
  readonly deviceId: string;
  /**
   * Absolute first, relative second. 200 CZK short on 2 000 is a different
   * event from 200 CZK on 80 000, and below the absolute floor nothing is
   * raised however large the percentage — raising every koruna trains everyone
   * to ignore the alert that matters.
   */
  readonly variancePolicy?: VariancePolicy;
}

const DEFAULT_POLICY: VariancePolicy = {
  watchAbsoluteMinor: 2_000n, // 20 Kč
  alertAbsoluteMinor: 20_000n, // 200 Kč
  watchBp: 100, // 1 %
  alertBp: 500, // 5 %
};

export interface DrawerResult {
  readonly ok: boolean;
  /** Translatable. The till never invents an English sentence for a Czech server. */
  readonly messageKey?: string;
}

/** What `restore` replays into the fold rather than into the cash tally. */
const SHIFT_TYPES = new Set(["shift.opened", "cash.moved", "shift.counted", "shift.closed"]);

const ok: DrawerResult = { ok: true };
const no = (messageKey: string): DrawerResult => ({ ok: false, messageKey });

export interface ZReport {
  readonly shiftId: string;
  readonly businessDate: string;
  readonly cashierId: string;
  readonly openedAt: Date;
  readonly closedAt: Date | null;
  readonly expectedMinor: bigint;
  readonly countedMinor: bigint;
  readonly breakdown: DrawerBreakdown;
  readonly variance: VarianceAssessment;
  readonly anomalyCount: number;
  /**
   * The cash other tills put in this drawer — a waiter's phone that joined the
   * bar's — once the server has said; null when it could not be asked, and
   * then the Z is this till's cash alone.
   */
  readonly otherTillsMinor: bigint | null;
}

/** What a cash tender did to the physical contents of the drawer. */
interface CashEffect {
  /** The sale: the amount less its rounding. */
  readonly cashMinor: bigint;
  readonly roundingMinor: bigint;
  readonly tipMinor: bigint;
}

export class DrawerSession {
  readonly #deps: DrawerDeps;
  #events: ShiftEvent[] = [];

  /**
   * The shift CURRENTLY TAKING EVENTS, or null between shifts.
   *
   * Not "the last shift this device saw". That distinction was a real bug and
   * a reload was hiding it: a fresh page built a fresh session, so the next
   * morning's count-in worked by accident. Close the lunch shift and open the
   * dinner one without reloading — or, once the journal existed, at all — and
   * the till answered "the drawer is already open" forever.
   *
   * It also decides attribution. A cash tender rung after the close belongs to
   * the next shift, not to the book that was just balanced.
   */
  #streamId: string | null = null;

  /**
   * Cash tenders seen since the shift opened, by tender id.
   *
   * Keyed so a reversal can take the amount AND its rounding back out together
   * — the guest who said cash, watched the till round 234,50 to 235,00, and
   * then found a card. Take only the amount and the drawer stays over by the
   * rounding of a tender that no longer exists.
   */
  readonly #cashTenders = new Map<string, CashEffect>();
  /** Cash handed back on a return, by refund. */
  readonly #cashRefunds = new Map<string, bigint>();
  /** What the other tills took into this drawer, as the server saw it. */
  #otherTills: Required<CashSalesFacts> | null = null;

  constructor(deps: DrawerDeps) {
    this.#deps = deps;
  }

  // --- The emit path -------------------------------------------------------

  /**
   * Pass an event to the outbox, folding anything that touches the drawer.
   *
   * WRAPPED IN A TRY. This sits in the path of every sale, and a reporting
   * concern that can stop the till from selling is a worse failure than a
   * Z-report missing a line.
   */
  async observe(event: OutboundEvent): Promise<void> {
    try {
      if (this.#fold(event)) await this.#deps.journal?.append(event);
    } catch {
      // Deliberately swallowed. See above — and it now covers the journal
      // write too, for the same reason: a full disk must cost a line on the
      // Z-report, not the ability to take money.
    }
    await this.#deps.outbox.enqueue(event);
  }

  /**
   * Rebuild from the journal, WITHOUT re-emitting.
   *
   * Feeding these back through `observe` would enqueue the whole evening a
   * second time. The server would dedupe on event id, but the outbox would
   * carry the duplicates and the blocking FIFO behind them would stall on
   * events already accepted.
   *
   * A journal that will not load is an empty drawer rather than a dead till.
   * The shift is recoverable from the server; the evening is not recoverable
   * from a POS that refuses to start.
   */
  async restore(): Promise<void> {
    if (!this.#deps.journal) return;

    let rows: OutboundEvent[];
    try {
      rows = await this.#deps.journal.load();
    } catch (error) {
      console.error("drawer: the local journal is unreadable —", error);
      return;
    }

    for (const event of rows) {
      if (SHIFT_TYPES.has(event.type)) {
        // The stream id IS the shift id, set when the shift opened. Cleared
        // again below if the replay reaches the close, so a till restored after
        // the Z can open the next shift.
        this.#streamId = event.streamId;
        this.#events.push({
          eventId: event.eventId,
          occurredAt: event.occurredAt,
          actorId: this.#deps.deviceId,
          type: event.type,
          payload: event.payload as Record<string, unknown>,
        } as ShiftEvent);
        continue;
      }
      this.#fold(event);
    }

    if (this.state()?.closedAt != null) this.#streamId = null;
  }

  /** True when the event touched the drawer and is worth journalling. */
  #fold(event: OutboundEvent): boolean {
    // Nothing before the shift opened belongs to this drawer, or the first
    // shift of the day inherits whatever the till was holding from a session
    // nobody closed and the variance is unexplainable.
    if (this.#streamId === null) return false;

    const payload = event.payload as Record<string, unknown>;

    if (event.type === "tender.recorded") {
      // CASH only. A meal voucher is a settlement instrument with its own
      // reconciliation; counting it here makes the drawer look short by exactly
      // the vouchers taken. A card tip settles to the acquirer, not into the
      // till.
      if (payload["kind"] !== "CASH") return false;

      const tenderId = String(payload["tenderId"]);
      const amountMinor = BigInt(payload["amountMinor"] as bigint);
      // A CASH TIP IS IN THE DRAWER. Not revenue, and physically there —
      // exactly like rounding. Omit it and every server who takes cash tips
      // looks over every night in one direction, which is what a failed skim
      // looks like, so the control is discredited before it catches anything.
      const tipMinor = BigInt((payload["tipMinor"] as bigint | undefined) ?? 0n);
      const roundingMinor = BigInt((payload["roundingMinor"] as bigint | undefined) ?? 0n);

      // The tender's amount already INCLUDES its rounding (Σ amount == due +
      // Σ rounding), so the sale is the amount less it, and the two together
      // are exactly the coins handed over.
      this.#cashTenders.set(tenderId, {
        cashMinor: amountMinor - roundingMinor,
        roundingMinor,
        tipMinor,
      });
      return true;
    }

    if (event.type === "check.refunded") {
      // A return: the cash part of it is counted back out of this drawer. A
      // card return goes back through the terminal and never touches it.
      let cashMinor = 0n;
      for (const t of payload["tenders"] as { kind: string; amountMinor: bigint }[]) {
        if (t.kind === "CASH") cashMinor += BigInt(t.amountMinor);
      }
      if (cashMinor === 0n) return false;
      this.#cashRefunds.set(String(payload["refundId"]), cashMinor);
      return true;
    }

    if (event.type === "tender.voided") {
      // Journalled whether or not it matched: a reversal of a tender this
      // drawer never saw is a no-op here and still has to be replayed in order,
      // because the tender it reverses may be earlier in the same journal.
      this.#cashTenders.delete(String(payload["tenderId"]));
      return true;
    }

    return false;
  }

  /**
   * JOIN A DRAWER SOMEBODY ELSE ALREADY OPENED.
   *
   * The till was the only thing that ever opened a drawer, and it opened a NEW
   * one every time somebody counted in. In the SHARED_TILL model — which is
   * what almost every Czech pub runs — one physical drawer takes every till's
   * cash, so a second open shift on it is not a second drawer: it is the same
   * money split between two Z-reports that each balance against half of it.
   *
   * The server sees two drawers open at once, refuses to guess which one a
   * tender belongs to, and the cash lands on NO Z-report at all. Which is the
   * honest answer to a question the till should not have asked.
   *
   * REFUSES IF THIS TILL ALREADY HAS ONE OPEN. Its own events are unsent work
   * and adopting over them would discard a float somebody counted by hand.
   * Refuses a CLOSED shift too: a drawer that has been counted down is not one
   * to join, it is one to open a successor to.
   */
  adopt(shiftId: string, events: readonly ShiftEvent[]): DrawerResult {
    if (this.#streamId !== null) return no("pos.drawer.alreadyOpen");
    if (events.length === 0) return no("pos.drawer.unknownShift");

    const folded = foldShift([...events]);
    if (!folded) return no("pos.drawer.unknownShift");
    // A counted-down drawer is finished with. Joining it would let a till ring
    // cash into a Z-report somebody has already signed.
    if (folded.closedAt !== null) return no("pos.drawer.alreadyClosed");

    this.#events = [...events];
    this.#streamId = shiftId;
    this.#otherTills = null;
    return ok;
  }

  /**
   * The other tills' cash in this drawer, fetched when it is counted out; null
   * when the server could not be asked. Only the server saw them all.
   */
  includeOtherTills(facts: Required<CashSalesFacts> | null): void {
    this.#otherTills = facts;
  }

  // --- Commands ------------------------------------------------------------

  async openShift(args: {
    drawerModel: DrawerModel;
    drawerId: string;
    cashierId: string;
    openingFloat: readonly DenominationCount[];
  }): Promise<DrawerResult> {
    if (this.#streamId !== null) return no("pos.drawer.alreadyOpen");

    /**
     * THE BUSINESS DATE FIRST, AND A REFUSAL IF IT IS NOT KNOWABLE.
     *
     * It comes from the venue clock in the signed config bundle, so a till that
     * has never synced genuinely cannot name its trading day — and a shift
     * filed on a guessed date lands on a day no report looks at.
     *
     * Refused rather than thrown. `businessDate` raises, and without this the
     * rejection escaped `openShift` entirely: a server tapping "count in"
     * before the first sync got an unhandled promise rejection and a screen
     * that did nothing, instead of a sentence telling them what is wrong.
     */
    let businessDate: string;
    try {
      businessDate = this.#deps.businessDate();
    } catch {
      return no("pos.drawer.needsConfig");
    }

    const shiftId = this.#deps.newId();
    this.#streamId = shiftId;

    // A NEW BOOK, not an addition to the old one. Without this the fold sees
    // yesterday's opener first, records ALREADY_OPEN against a clean shift, and
    // reports the previous cashier and float.
    this.#events = [];
    this.#cashTenders.clear();
    this.#cashRefunds.clear();
    this.#otherTills = null;

    // CLEARED FIRST. Otherwise tomorrow's drawer inherits yesterday's float and
    // takings and the variance is unexplainable. The old events are already
    // durable in the outbox and on the server; this journal is only local.
    try {
      await this.#deps.journal?.reset();
    } catch (error) {
      console.error("drawer: could not clear the local journal —", error);
    }

    await this.#emit("shift.opened", shiftId, {
      shiftId,
      businessDate,
      drawerModel: args.drawerModel,
      drawerId: args.drawerId,
      cashierId: args.cashierId,
      openingFloat: args.openingFloat.map((d) => ({ ...d })),
    });
    return ok;
  }

  async move(args: {
    kind: CashMovementKind;
    amountMinor: bigint;
    reasonCode: string;
    bagId?: string;
    witnessedBy?: string;
  }): Promise<DrawerResult> {
    const open = this.#openState();
    if (!open) return no("pos.drawer.notOpen");

    // Direction is carried by `kind`, never by a sign: a negative pay-out is a
    // pay-in with worse provenance.
    if (args.amountMinor <= 0n) return no("pos.drawer.amountRequired");
    if (!args.reasonCode.trim()) return no("pos.drawer.reasonRequired");
    if (args.kind === "SAFE_DROP" && (!args.bagId?.trim() || !args.witnessedBy?.trim())) {
      // A drop that cannot be reconciled against tomorrow's bank credit is not
      // a control, it is a hole with paperwork attached.
      return no("pos.drawer.dropNeedsWitness");
    }

    await this.#emit("cash.moved", open.shiftId, {
      movementId: this.#deps.newId(),
      kind: args.kind,
      amountMinor: args.amountMinor,
      reasonCode: args.reasonCode.trim(),
      ...(args.bagId ? { bagId: args.bagId } : {}),
      ...(args.witnessedBy ? { witnessedBy: args.witnessedBy } : {}),
    });
    return ok;
  }

  async count(args: {
    kind: CountKind;
    denominations: readonly DenominationCount[];
    countedBy: string;
  }): Promise<DrawerResult> {
    const open = this.#openState();
    if (!open) return no("pos.drawer.notOpen");

    const bad = args.denominations.find(
      (d) => !(CZK_DENOMINATIONS as readonly number[]).includes(d.denominationMinor) || d.count < 0,
    );
    if (bad) return no("pos.drawer.countInvalid");

    await this.#emit("shift.counted", open.shiftId, {
      countId: this.#deps.newId(),
      kind: args.kind,
      denominations: args.denominations.map((d) => ({ ...d })),
      countedBy: args.countedBy,
    });
    return ok;
  }

  async closeShift(): Promise<DrawerResult> {
    const open = this.#openState();
    if (!open) return no("pos.drawer.notOpen");
    // NO_CLOSING_COUNT is an anomaly the fold records after the fact. Asking
    // for the count is better than filing the anomaly.
    if (!open.counts.some((c) => c.kind === "CLOSE")) return no("pos.drawer.countRequired");

    await this.#emit("shift.closed", open.shiftId, { shiftId: open.shiftId });
    // The book is closed, so it takes nothing more — and the next shift can be
    // opened. `report()` still reads the events, so the Z stays on screen.
    this.#streamId = null;
    return ok;
  }

  // --- Reads ---------------------------------------------------------------

  state(): ShiftState | undefined {
    return foldShift(this.#events);
  }

  /** Every denomination at zero. Pre-filling from the float would be a hint. */
  countSheet(kind: CountKind): CountSheet {
    const state = this.state();
    if (!state) {
      return { shiftId: "", kind, lines: CZK_DENOMINATIONS.map((d) => ({ denominationMinor: d, count: 0 })) };
    }
    return countSheetFor(state, kind);
  }

  /**
   * The Z, or null while the count is still owed.
   *
   * The structural half of the blind guarantee on this side. A MID_SHIFT count
   * does not unlock it — a surprise count must not hand the cashier the
   * expected total for the rest of the evening.
   */
  report(): ZReport | null {
    const state = this.state();
    if (!state) return null;

    const closing = state.counts.filter((c) => c.kind === "CLOSE");
    if (closing.length === 0) return null;

    const { expectedMinor, breakdown } = expectedDrawer(state, this.#cashFacts());
    const o = this.#otherTills;
    // The LAST count, because a recount is a new record rather than an edit —
    // and the first is still in `state.counts` as evidence that it happened.
    const countedMinor = countTotal(closing.at(-1)!.denominations);

    return {
      shiftId: state.shiftId,
      businessDate: state.businessDate,
      cashierId: state.cashierId,
      openedAt: state.openedAt,
      closedAt: state.closedAt,
      expectedMinor,
      countedMinor,
      breakdown,
      variance: classifyVariance(
        countedMinor - expectedMinor,
        expectedMinor,
        this.#deps.variancePolicy ?? DEFAULT_POLICY,
      ),
      anomalyCount: state.anomalies.length,
      otherTillsMinor: o && o.cashSalesMinor + o.cashRoundingMinor + o.cashTipsMinor - o.cashRefundsMinor,
    };
  }

  // --- Internals -----------------------------------------------------------

  /** This till's cash, plus what the other tills put in the drawer when known. */
  #cashFacts(): Required<CashSalesFacts> {
    const f = { cashSalesMinor: 0n, cashRefundsMinor: 0n, cashRoundingMinor: 0n, cashTipsMinor: 0n, ...this.#otherTills };
    for (const e of this.#cashTenders.values()) {
      f.cashSalesMinor += e.cashMinor;
      f.cashRoundingMinor += e.roundingMinor;
      f.cashTipsMinor += e.tipMinor;
    }
    // A reversed tender is not a refund — it is a tender that never happened,
    // so it leaves through `#cashTenders`. A return is money that did happen
    // and then went back.
    for (const amount of this.#cashRefunds.values()) f.cashRefundsMinor += amount;
    return f;
  }


  /** The folded state, but only while it is still taking events. */
  #openState(): ShiftState | undefined {
    const state = this.state();
    return state && state.closedAt === null ? state : undefined;
  }

  async #emit(type: string, shiftId: string, payload: Record<string, unknown>): Promise<void> {
    const event: OutboundEvent = {
      eventId: this.#deps.newId(),
      // THE SHIFT ID IS THE STREAM ID. Every event of this shift has to land on
      // the same stream or the server folds an opener with nothing after it.
      streamId: shiftId,
      type,
      payload,
      hlc: this.#deps.nextHlc(),
      occurredAt: this.#deps.now(),
    };

    // Folded locally FIRST so the screen is right even while the event is still
    // queued — which, on a till that has been offline since lunch, is the whole
    // evening.
    this.#events.push({
      eventId: event.eventId,
      occurredAt: event.occurredAt,
      actorId: this.#deps.deviceId,
      type,
      payload,
    } as ShiftEvent);

    try {
      await this.#deps.journal?.append(event);
    } catch (error) {
      console.error("drawer: the local journal refused a write —", error);
    }

    await this.#deps.outbox.enqueue(event);
  }
}
